import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { api } from '../../lib/api';
import { 
  Bell, 
  X, 
  Check, 
  CheckCheck,
  Trash2,
  Users,
  AlertTriangle,
  Book,
  Server,
  Trophy,
  Activity,
  RefreshCw,
  Sparkles,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { InfinityLoader } from '../../components/InfinityLoader';

const ICON_MAP = {
  'users': Users,
  'alert-triangle': AlertTriangle,
  'book': Book,
  'server': Server,
  'trophy': Trophy,
  'activity': Activity,
  'bell': Bell,
};

const COLOR_MAP = {
  'emerald': 'bg-tan-900 border-tan-500/30 text-tan-400',
  'red': 'bg-red-950 border-red-500/30 text-red-400',
  'blue': 'bg-blue-950 border-blue-500/30 text-blue-400',
  'amber': 'bg-amber-950 border-amber-500/30 text-amber-400',
  'violet': 'bg-violet-950 border-violet-500/30 text-violet-400',
  'slate': 'bg-slate-800 border-slate-500/30 text-slate-400',
};

function formatTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function NotificationCenter() {
  const { token } = useAdminAuth();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingTriggers, setCheckingTriggers] = useState(false);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);
  const rootRef = useRef(null);
  const drawerRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const triggerCheckRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.adminGetNotifications(token, 20, false);
      setNotifications(response.data.notifications || []);
      
      const newUnreadCount = response.data.unread_count || 0;
      
      // Show toast for new notifications
      if (newUnreadCount > previousUnreadCount && previousUnreadCount > 0) {
        const newCount = newUnreadCount - previousUnreadCount;
        toast.info(`${newCount} new notification${newCount > 1 ? 's' : ''}`, {
          icon: <Bell className="w-4 h-4" />,
        });
      }
      
      setPreviousUnreadCount(newUnreadCount);
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }, [token, previousUnreadCount]);

  // Initial load
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    pollIntervalRef.current = setInterval(() => {
      loadNotifications();
    }, 30000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [loadNotifications]);

  // Check triggers every 5 minutes (300 seconds)
  useEffect(() => {
    const checkTriggers = async () => {
      if (!token) return;
      try {
        const response = await api.adminCheckTriggers(token);
        if (response.data.notifications_created > 0) {
          loadNotifications();
        }
      } catch (error) {
        // Silently fail - triggers will be checked on next interval
      }
    };

    // Check triggers on initial load
    checkTriggers();

    // Then check every 5 minutes
    triggerCheckRef.current = setInterval(checkTriggers, 300000);

    return () => {
      if (triggerCheckRef.current) {
        clearInterval(triggerCheckRef.current);
      }
    };
  }, [token, loadNotifications]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        rootRef.current
        && !rootRef.current.contains(event.target)
        && !drawerRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleMarkRead = async (notificationId) => {
    try {
      await api.adminMarkNotificationRead(token, notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.adminMarkAllNotificationsRead(token);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      const deletedNotification = notifications.find(n => n.id === notificationId);
      await api.adminDeleteNotification(token, notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (deletedNotification && !deletedNotification.read) {
        setUnreadCount(count => Math.max(0, count - 1));
      }
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleGenerateSample = async () => {
    setLoading(true);
    try {
      await api.adminGenerateSampleNotifications(token);
      await loadNotifications();
      toast.success('Sample notifications generated');
    } catch (error) {
      console.error('Failed to generate notifications:', error);
      toast.error('Failed to generate notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckTriggers = async () => {
    setCheckingTriggers(true);
    try {
      const response = await api.adminCheckTriggers(token);
      const { notifications_created, fired_triggers } = response.data;
      
      if (notifications_created > 0) {
        await loadNotifications();
        toast.success(`${notifications_created} trigger${notifications_created > 1 ? 's' : ''} fired!`, {
          description: fired_triggers.map(t => t.title).join(', ')
        });
      } else {
        toast.info('No triggers fired - all metrics within normal ranges');
      }
    } catch (error) {
      console.error('Failed to check triggers:', error);
      toast.error('Failed to check triggers');
    } finally {
      setCheckingTriggers(false);
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell-btn"
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={isOpen}
        className="relative p-2 admin-surface-secondary hover:bg-slate-800 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-violet-400/70"
      >
        <Bell className="w-5 h-5 text-slate-400" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs 
                       font-bold rounded-full flex items-center justify-center"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
            ref={drawerRef}
            className="fixed right-3 top-3 z-[100] flex h-[calc(100vh-24px)] w-[380px] max-w-[calc(100vw-24px)]
                       flex-col overflow-hidden rounded-2xl border border-slate-700 bg-[#080C10]
                       shadow-[0_28px_90px_rgba(0,0,0,0.78)]"
            data-testid="notification-panel"
            role="dialog"
            aria-modal="false"
            aria-labelledby="notification-panel-title"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 border-b border-slate-700 bg-[#0B1016] p-5">
              <div>
                <h3 id="notification-panel-title" className="text-lg font-semibold text-[#F2F4F7]">Notifications</h3>
                <p className="mt-1 text-sm text-[#9BA3AE]">
                  {unreadCount} unread notification{unreadCount === 1 ? '' : 's'}
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-lg p-2 text-[#9BA3AE] transition-colors hover:bg-slate-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-violet-400/70"
                aria-label="Close notifications"
                data-testid="notification-panel-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex items-center justify-between gap-3 border-b border-slate-800 bg-[#080C10] px-5 py-3">
              <button
                onClick={handleMarkAllRead}
                disabled={unreadCount === 0}
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-violet-300 transition-colors hover:bg-violet-950 hover:text-violet-200 disabled:cursor-not-allowed disabled:text-slate-600 disabled:hover:bg-slate-900"
              >
                <CheckCheck className="h-4 w-4" />
                Mark all read
              </button>
              <button
                onClick={loadNotifications}
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-[#9BA3AE] transition-colors hover:bg-slate-800 hover:text-white"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>

            {/* Notification List */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {notifications.length === 0 ? (
                <div className="flex h-full min-h-64 flex-col items-center justify-center p-8 text-center">
                  <div className="mb-4 rounded-2xl border border-slate-700 bg-slate-900 p-4">
                    <Bell className="h-7 w-7 text-slate-500" />
                  </div>
                  <p className="font-medium text-[#F2F4F7]">No notifications</p>
                  <p className="mt-1 text-sm text-[#9BA3AE]">New admin alerts will appear here.</p>
                </div>
              ) : (
                <div className="space-y-3 p-4">
                  {notifications.map((notification) => {
                    const IconComponent = ICON_MAP[notification.config?.icon] || Bell;
                    const colorClass = COLOR_MAP[notification.config?.color] || COLOR_MAP.slate;
                    
                    return (
                      <div
                        key={notification.id}
                        className={`rounded-xl border p-4 transition-colors ${
                          !notification.read
                            ? 'border-violet-500/40 bg-[#111827]'
                            : 'border-slate-800 bg-[#0B1016] hover:border-slate-700'
                        }`}
                        data-testid={`notification-${notification.id}`}
                      >
                        <div className="flex gap-3">
                          {/* Icon */}
                          <div className={`p-2 rounded-xl ${colorClass} border flex-shrink-0`}>
                            <IconComponent className="w-4 h-4" />
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className={`font-medium text-sm ${
                                notification.read ? 'text-slate-300' : 'text-white'
                              }`}>
                                {notification.title}
                              </h4>
                              {!notification.read && (
                                <span className="w-2 h-2 bg-violet-500 rounded-full flex-shrink-0 mt-1.5" />
                              )}
                            </div>
                            <p className="mt-1 text-sm leading-5 text-[#9BA3AE]">
                              {notification.message}
                            </p>
                            <div className="mt-3 flex items-center justify-between gap-3">
                              <span
                                className="text-xs text-slate-500"
                                title={new Date(notification.created_at).toLocaleString()}
                              >
                                {formatTimeAgo(notification.created_at)}
                              </span>
                              <div className="flex items-center gap-2">
                                {!notification.read && (
                                  <button
                                    onClick={() => handleMarkRead(notification.id)}
                                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-slate-400 transition-colors hover:bg-slate-800 hover:text-tan-300"
                                    title="Mark as read"
                                  >
                                    <Check className="h-3.5 w-3.5" />
                                    Read
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDelete(notification.id)}
                                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-slate-400 transition-colors hover:bg-red-950 hover:text-red-300"
                                  title="Delete"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-slate-800 bg-[#0B1016] px-4 py-3">
              <button
                onClick={handleCheckTriggers}
                disabled={checkingTriggers}
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs text-slate-400 transition-colors hover:bg-slate-800 hover:text-amber-300"
              >
                {checkingTriggers ? <InfinityLoader size={32} /> : <Zap className="h-3.5 w-3.5" />}
                Check triggers
              </button>
              <button
                onClick={handleGenerateSample}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs text-slate-400 transition-colors hover:bg-slate-800 hover:text-violet-300"
              >
                {loading ? <InfinityLoader size={32} /> : <Sparkles className="h-3.5 w-3.5" />}
                Generate samples
              </button>
            </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default NotificationCenter;
